import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAdminContext } from '../contexts/AdminContextProvider';
import TenantContextDropdown from '../components/common/TenantContextDropdown';
import {
  SearchIcon,
  RefreshIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  SecurityIcon,
  BuildingIcon,
  AlertIcon,
  CheckIcon
} from '../components/common/Icons';

interface Role {
  id: string;
  name: string;
  display_name: string;
  description: string;
  permissions: string[];
  tab_access: string[];
  can_create_roles?: string[];
  functional_areas?: string[]; // WQ-309: RAG retrieval area grants (area_key strings)
  user_count: number;
  is_system: boolean;
  tenant_id?: string; // For tenant-specific roles
}

interface Permission {
  id: string;
  name: string;
  category: string;
}

interface Tab {
  id: string;
  name: string;
  icon: string;
}

// WQ-309: a functional-area option (RAG retrieval grant vocabulary).
// Sourced LIVE from /api/admin/roles/functional-areas (the canonical taxonomy)
// so the option list stays in sync — never hardcoded here.
interface FunctionalArea {
  area_key: string;
  label: string;
}

// Permission categories for the Create Role modal
const PERMISSION_CATEGORIES = [
  {
    id: 'Tabs',
    name: 'Tab Access',
    permissions: [
      { id: 'tab.dashboard', name: 'View Dashboard' },
      { id: 'tab.configure', name: 'View Configure' },
      { id: 'tab.llm', name: 'View LLM Management' },
      { id: 'tab.docs', name: 'View Documentation' },
      { id: 'tab.schema', name: 'View Schema' },
      { id: 'tab.chat', name: 'View Chat' },
      { id: 'tab.users', name: 'View Users Tab' },
      { id: 'tab.audit', name: 'View Audit Tab' },
      { id: 'tab.roles', name: 'View Roles Tab' },
    ]
  },
  {
    id: 'Users',
    name: 'User Management',
    permissions: [
      { id: 'users.view', name: 'View Users' },
      { id: 'users.create', name: 'Create Users' },
      { id: 'users.edit', name: 'Edit Users' },
      { id: 'users.delete', name: 'Delete Users' },
      { id: 'users.reset_password', name: 'Reset User Passwords' },
    ]
  },
  {
    id: 'Roles',
    name: 'Role Management',
    permissions: [
      { id: 'roles.view', name: 'View Roles' },
      { id: 'roles.create', name: 'Create Roles' },
      { id: 'roles.edit', name: 'Edit Roles' },
      { id: 'roles.delete', name: 'Delete Roles' },
      { id: 'roles.assign', name: 'Assign Roles' },
    ]
  },
  {
    id: 'Audit',
    name: 'Audit Logs',
    permissions: [
      { id: 'audit.view_own', name: 'View Own Audit Logs' },
      { id: 'audit.view_all', name: 'View All Audit Logs' },
      { id: 'audit.export', name: 'Export Audit Logs' },
    ]
  },
  {
    id: 'System',
    name: 'System Administration',
    permissions: [
      { id: 'system.configure', name: 'Configure System' },
      { id: 'system.email', name: 'Configure Email' },
      { id: 'system.sso', name: 'Configure SSO' },
      { id: 'system.backup', name: 'Manage Backups' },
    ]
  },
  {
    id: 'LLM',
    name: 'LLM Management',
    permissions: [
      { id: 'llm.view', name: 'View LLM Models' },
      { id: 'llm.register', name: 'Register LLM Models' },
      { id: 'llm.deploy', name: 'Deploy LLM Models' },
    ]
  },
];

// Available tabs for tab_access field
const AVAILABLE_TABS: Tab[] = [
  { id: 'dashboard', name: 'Dashboard', icon: 'dashboard' },
  { id: 'configure', name: 'Configure', icon: 'settings' },
  { id: 'llm', name: 'LLM Management', icon: 'smart_toy' },
  { id: 'docs', name: 'Documentation', icon: 'description' },
  { id: 'schema', name: 'Schema', icon: 'storage' },
  { id: 'chat', name: 'Chat', icon: 'chat' },
  { id: 'users', name: 'Users', icon: 'group' },
  { id: 'audit', name: 'Audit', icon: 'history' },
  { id: 'roles', name: 'Roles', icon: 'admin_panel_settings' },
  { id: 'email', name: 'Email', icon: 'email' },
];

// Roles that can be assigned the "can create" permission
const ASSIGNABLE_ROLES = ['admin', 'tenant_admin', 'operator', 'user'];

const RolesPage: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const { activeTenant, isInTenantContext } = useAdminContext();
  const [roles, setRoles] = useState<Role[]>([]);
  const [_permissions, setPermissions] = useState<Permission[]>([]);
  const [functionalAreas, setFunctionalAreas] = useState<FunctionalArea[]>([]); // WQ-309 (live)
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Create Role Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: [] as string[],
    tab_access: [] as string[],
    can_create_roles: [] as string[],
    functional_areas: [] as string[], // WQ-309
  });
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit Role Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editForm, setEditForm] = useState({
    display_name: '',
    description: '',
    permissions: [] as string[],
    tab_access: [] as string[],
    can_create_roles: [] as string[],
    functional_areas: [] as string[], // WQ-309
  });
  const [editError, setEditError] = useState<string | null>(null);

  // Confirmation Dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState<{
    title: string;
    message: string;
    confirmText: string;
    confirmVariant: 'danger' | 'primary';
    onConfirm: () => void;
  } | null>(null);

  // Error Dialog state
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorDialogConfig, setErrorDialogConfig] = useState<{
    title: string;
    message: string;
    details?: string;
    suggestion?: string;
  } | null>(null);

  // Check if current user is a Super Admin (for system role editing)
  const isSuperAdmin = (): boolean => {
    const userRole = localStorage.getItem('user_role') || '';
    return userRole.toLowerCase() === 'superadmin' || userRole.toLowerCase() === 'super_admin';
  };

  // Check if a role can be edited by the current user
  const canEditRole = (role: Role): boolean => {
    // Custom roles can be edited by anyone with roles.edit permission
    if (!role.is_system) return true;
    // System roles require Super Admin
    return isSuperAdmin();
  };

  // Slug validation: lowercase, alphanumeric, underscores, must start with letter
  const isValidSlug = (slug: string): boolean => {
    return /^[a-z][a-z0-9_]*$/.test(slug);
  };

  // Auto-generate slug from display name
  const generateSlug = (displayName: string): string => {
    return displayName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .replace(/^[0-9_]+/, ''); // Remove leading numbers or underscores
  };

  // Toggle permission in create form
  const togglePermission = (permissionId: string) => {
    setCreateForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  // Toggle tab access in create form
  const toggleTabAccess = (tabId: string) => {
    setCreateForm(prev => ({
      ...prev,
      tab_access: prev.tab_access.includes(tabId)
        ? prev.tab_access.filter(t => t !== tabId)
        : [...prev.tab_access, tabId]
    }));
  };

  // Toggle can_create_roles in create form
  const toggleCanCreateRole = (roleName: string) => {
    setCreateForm(prev => ({
      ...prev,
      can_create_roles: prev.can_create_roles.includes(roleName)
        ? prev.can_create_roles.filter(r => r !== roleName)
        : [...prev.can_create_roles, roleName]
    }));
  };

  // Toggle functional-area grant in create form (WQ-309)
  const toggleFunctionalArea = (areaKey: string) => {
    setCreateForm(prev => ({
      ...prev,
      functional_areas: prev.functional_areas.includes(areaKey)
        ? prev.functional_areas.filter(a => a !== areaKey)
        : [...prev.functional_areas, areaKey]
    }));
  };

  // Reset create form
  const resetCreateForm = () => {
    setCreateForm({
      name: '',
      display_name: '',
      description: '',
      permissions: [],
      tab_access: [],
      can_create_roles: [],
      functional_areas: [],
    });
    setCreateError(null);
  };

  // Toggle permission in edit form
  const toggleEditPermission = (permissionId: string) => {
    setEditForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  // Toggle tab access in edit form
  const toggleEditTabAccess = (tabId: string) => {
    setEditForm(prev => ({
      ...prev,
      tab_access: prev.tab_access.includes(tabId)
        ? prev.tab_access.filter(t => t !== tabId)
        : [...prev.tab_access, tabId]
    }));
  };

  // Toggle can_create_roles in edit form
  const toggleEditCanCreateRole = (roleName: string) => {
    setEditForm(prev => ({
      ...prev,
      can_create_roles: prev.can_create_roles.includes(roleName)
        ? prev.can_create_roles.filter(r => r !== roleName)
        : [...prev.can_create_roles, roleName]
    }));
  };

  // Toggle functional-area grant in edit form (WQ-309)
  const toggleEditFunctionalArea = (areaKey: string) => {
    setEditForm(prev => ({
      ...prev,
      functional_areas: prev.functional_areas.includes(areaKey)
        ? prev.functional_areas.filter(a => a !== areaKey)
        : [...prev.functional_areas, areaKey]
    }));
  };

  // Open edit modal with role data
  const handleEditRole = (role: Role) => {
    if (!canEditRole(role)) {
      alert('You do not have permission to edit system roles. Only Super Admins can edit system roles.');
      return;
    }
    setEditingRole(role);
    setEditForm({
      display_name: role.display_name,
      description: role.description || '',
      permissions: role.permissions || [],
      tab_access: role.tab_access || [],
      can_create_roles: role.can_create_roles || [],
      functional_areas: role.functional_areas || [],
    });
    setEditError(null);
    setShowEditModal(true);
  };

  // Handle update role submission
  const handleUpdateRole = async () => {
    if (!editingRole) return;

    if (!editForm.display_name.trim()) {
      setEditError('Display name is required');
      return;
    }

    setIsUpdating(true);
    setEditError(null);

    try {
      const response = await authFetch(`/api/admin/roles/${editingRole.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          display_name: editForm.display_name.trim(),
          description: editForm.description.trim() || null,
          permissions: editForm.permissions,
          tab_access: editForm.tab_access,
          can_create_roles: editForm.can_create_roles,
          functional_areas: editForm.functional_areas,
        })
      });

      if (response.ok) {
        setShowEditModal(false);
        setEditingRole(null);
        fetchRoles();
      } else {
        const errorData = await response.json();
        const errorMessage = typeof errorData.detail === 'object'
          ? errorData.detail.message || JSON.stringify(errorData.detail)
          : errorData.detail || 'Failed to update role';
        setEditError(errorMessage);
      }
    } catch (err) {
      setEditError('Failed to update role. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle create role submission
  const handleCreateRole = async () => {
    // Validation
    if (!createForm.name.trim()) {
      setCreateError('Role name (slug) is required');
      return;
    }
    if (!isValidSlug(createForm.name)) {
      setCreateError('Role name must start with a letter and contain only lowercase letters, numbers, and underscores');
      return;
    }
    if (!createForm.display_name.trim()) {
      setCreateError('Display name is required');
      return;
    }

    setIsCreating(true);
    setCreateError(null);

    try {
      const response = await authFetch('/api/admin/roles', {
        method: 'POST',
        body: JSON.stringify({
          name: createForm.name.trim(),
          display_name: createForm.display_name.trim(),
          description: createForm.description.trim() || null,
          permissions: createForm.permissions,
          tab_access: createForm.tab_access,
          can_create_roles: createForm.can_create_roles,
          functional_areas: createForm.functional_areas,
        })
      });

      if (response.ok) {
        setShowCreateModal(false);
        resetCreateForm();
        fetchRoles();
      } else {
        const errorData = await response.json();
        const errorMessage = typeof errorData.detail === 'object'
          ? errorData.detail.message || JSON.stringify(errorData.detail)
          : errorData.detail || 'Failed to create role';
        setCreateError(errorMessage);
      }
    } catch (err) {
      setCreateError('Failed to create role. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  // Note: Tenant-specific roles API will be implemented in a future phase
  // For now, we show platform roles with a notice when in tenant context
  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const apiPath = isInTenantContext && activeTenant
        ? `/api/admin/tenants/${activeTenant.id}/roles`
        : '/api/admin/roles';

      const response = await authFetch(apiPath);
      if (response.ok) {
        const data = await response.json();
        // Handle both array and wrapped response formats
        setRoles(Array.isArray(data) ? data : data.roles || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || `Failed to fetch roles: ${response.status}`);
      }
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      setError('Failed to connect to API');
    }
    setIsLoading(false);
  }, [authFetch]);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await authFetch('/api/admin/roles/permissions');
      if (response.ok) {
        const data = await response.json();
        setPermissions(Array.isArray(data) ? data : data.permissions || []);
      }
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
    }
  }, [authFetch]);

  // WQ-309: live functional-area list (the canonical taxonomy) for the grant
  // multi-select — kept in sync with the backend so the option list never drifts.
  const fetchFunctionalAreas = useCallback(async () => {
    try {
      const response = await authFetch('/api/admin/roles/functional-areas');
      if (response.ok) {
        const data = await response.json();
        setFunctionalAreas(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Failed to fetch functional areas:', err);
    }
  }, [authFetch]);

  useEffect(() => {
    // Wait for auth to be ready before fetching
    if (authLoading) return;
    fetchRoles();
    fetchPermissions();
    fetchFunctionalAreas();
  }, [authLoading, fetchRoles, fetchPermissions, fetchFunctionalAreas]);

  // Show confirmation dialog for role deletion
  const confirmDeleteRole = (role: Role) => {
    setConfirmDialogConfig({
      title: 'Delete Role',
      message: `Are you sure you want to delete the "${role.display_name}" role? This action cannot be undone.`,
      confirmText: 'Delete Role',
      confirmVariant: 'danger',
      onConfirm: () => executeDeleteRole(role.id),
    });
    setShowConfirmDialog(true);
  };

  // Execute the actual role deletion
  const executeDeleteRole = async (roleId: string) => {
    setShowConfirmDialog(false);
    setConfirmDialogConfig(null);

    try {
      const response = await authFetch(`/api/admin/roles/${roleId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        fetchRoles();
      } else {
        const errorData = await response.json();
        // Handle multiple error formats: { detail: "..." } or { error: { message: "..." } }
        const errorMessage = errorData.detail || errorData.error?.message || 'Failed to delete role';

        // Check if error is about users having this role
        const userCountMatch = errorMessage.match(/(\d+)\s+users?\s+have/i);
        if (userCountMatch) {
          const userCount = userCountMatch[1];
          setErrorDialogConfig({
            title: 'Cannot Delete Role',
            message: `This role is currently assigned to ${userCount} user${parseInt(userCount) > 1 ? 's' : ''}.`,
            details: 'Roles with assigned users cannot be deleted to prevent users from losing access.',
            suggestion: 'To delete this role, first reassign all users to a different role from the Users page, then try again.',
          });
        } else if (errorMessage.toLowerCase().includes('system role')) {
          setErrorDialogConfig({
            title: 'Cannot Delete System Role',
            message: 'System roles (superadmin, admin, operator, user) cannot be deleted.',
            details: 'These roles are required for the platform to function properly.',
          });
        } else {
          setErrorDialogConfig({
            title: 'Error Deleting Role',
            message: errorMessage,
          });
        }
        setShowErrorDialog(true);
      }
    } catch (err) {
      setErrorDialogConfig({
        title: 'Network Error',
        message: 'Failed to connect to the server. Please check your connection and try again.',
      });
      setShowErrorDialog(true);
    }
  };

  // Legacy handler for backward compatibility
  const handleDeleteRole = (roleId: string) => {
    const role = roles.find(r => r.id === roleId);
    if (role) {
      confirmDeleteRole(role);
    }
  };

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    role.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (role.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Context-aware labels
  const pageTitle = isInTenantContext ? 'Tenant Role Management' : 'Role Management';
  const pageSubtitle = isInTenantContext && activeTenant
    ? `Configure roles for ${activeTenant.name} (${roles.length} roles)`
    : `Configure roles and permissions (${roles.length} roles)`;

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 className="page-title">{pageTitle}</h1>
            <TenantContextDropdown />
            {/* Context Indicator Badge */}
            {isInTenantContext && activeTenant ? (
              <span
                className="badge"
                style={{
                  background: activeTenant.branding?.primaryColor || 'var(--primary)',
                  color: '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                <BuildingIcon size={12} />
                {activeTenant.name}
              </span>
            ) : (
              <span
                className="badge"
                style={{
                  background: 'rgba(59, 130, 246, 0.15)',
                  color: '#3b82f6',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                Platform Level
              </span>
            )}
          </div>
          <p className="page-subtitle">{pageSubtitle}</p>
        </div>
        <div className="page-header-right">
          <button
            className="btn btn-primary"
            disabled={isInTenantContext}
            onClick={() => {
              resetCreateForm();
              setShowCreateModal(true);
            }}
            title="Create a new role"
          >
            <PlusIcon size={16} />
            Create Role
          </button>
          <button className="btn btn-ghost" onClick={fetchRoles}>
            <RefreshIcon size={18} />
          </button>
        </div>
      </div>

      {/* Tenant context notice */}
      {isInTenantContext && (
        <div className="alert alert-info" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertIcon size={18} />
          <span>
            Showing roles available for this tenant. Tenant users can be assigned these roles.
          </span>
        </div>
      )}

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div className="page-toolbar">
        <div className="input-wrapper" style={{ maxWidth: '320px' }}>
          <SearchIcon size={18} className="input-icon" />
          <input
            type="text"
            className="input input-with-icon"
            placeholder="Search roles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="glass-card">
        {isLoading ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading roles...</p>
          </div>
        ) : filteredRoles.length === 0 ? (
          <div className="empty-state">
            <SecurityIcon size={48} />
            <p>No roles found</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Description</th>
                <th>Permissions</th>
                <th>Users</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoles.map((role) => (
                <tr key={role.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="icon-badge">
                        <SecurityIcon size={16} />
                      </div>
                      <div>
                        <div className="font-medium">{role.display_name}</div>
                        <div className="text-secondary text-sm">{role.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-secondary">{role.description || '-'}</td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {(role.permissions || []).slice(0, 3).map((perm, i) => (
                        <span key={i} className="badge badge-secondary">{perm}</span>
                      ))}
                      {(role.permissions || []).length > 3 && (
                        <span className="badge badge-secondary">+{role.permissions.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td>{role.user_count}</td>
                  <td>
                    {role.is_system ? (
                      <span className="badge badge-info">System</span>
                    ) : (
                      <span className="badge badge-secondary">Custom</span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleEditRole(role)}
                        title={
                          role.is_system && !isSuperAdmin()
                            ? 'Only Super Admins can edit system roles'
                            : `Edit ${role.display_name}`
                        }
                        disabled={role.is_system && !isSuperAdmin()}
                        style={{
                          opacity: role.is_system && !isSuperAdmin() ? 0.5 : 1,
                          cursor: role.is_system && !isSuperAdmin() ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <EditIcon size={14} />
                      </button>
                      {!role.is_system && (
                        <button
                          className="btn btn-ghost btn-sm text-error"
                          onClick={() => handleDeleteRole(role.id)}
                        >
                          <TrashIcon size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Role Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div
            className="modal"
            style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Create New Role</h2>
              <button className="btn btn-ghost" onClick={() => setShowCreateModal(false)}>×</button>
            </div>

            <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
              {createError && (
                <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                  {createError}
                </div>
              )}

              {/* Basic Information */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
                  Basic Information
                </h3>
                <div className="form-group">
                  <label>Display Name *</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g., Content Manager"
                    value={createForm.display_name}
                    onChange={(e) => {
                      const displayName = e.target.value;
                      setCreateForm(prev => ({
                        ...prev,
                        display_name: displayName,
                        // Auto-generate slug if name is empty or was auto-generated
                        name: prev.name === '' || prev.name === generateSlug(prev.display_name)
                          ? generateSlug(displayName)
                          : prev.name
                      }));
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>Role Name (slug) *</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g., content_manager"
                    value={createForm.name}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value.toLowerCase() }))}
                  />
                  <small style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                    Lowercase letters, numbers, and underscores only. Must start with a letter.
                  </small>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="input"
                    placeholder="Optional description of this role's purpose"
                    rows={2}
                    value={createForm.description}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                    style={{ resize: 'vertical', minHeight: '60px' }}
                  />
                </div>
              </div>

              {/* Permissions */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
                  Permissions ({createForm.permissions.length} selected)
                </h3>
                <div style={{ display: 'grid', gap: '16px' }}>
                  {PERMISSION_CATEGORIES.map(category => (
                    <div
                      key={category.id}
                      style={{
                        padding: '12px',
                        background: 'var(--bg-secondary)',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                      }}
                    >
                      <div style={{ fontWeight: 500, marginBottom: '8px', fontSize: '13px' }}>
                        {category.name}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '6px' }}>
                        {category.permissions.map(perm => (
                          <label
                            key={perm.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              background: createForm.permissions.includes(perm.id)
                                ? 'rgba(59, 130, 246, 0.1)'
                                : 'transparent',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={createForm.permissions.includes(perm.id)}
                              onChange={() => togglePermission(perm.id)}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>{perm.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tab Access */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
                  Tab Access ({createForm.tab_access.length} selected)
                </h3>
                <div
                  style={{
                    padding: '12px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                  }}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '6px' }}>
                    {AVAILABLE_TABS.map(tab => (
                      <label
                        key={tab.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          background: createForm.tab_access.includes(tab.id)
                            ? 'rgba(59, 130, 246, 0.1)'
                            : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={createForm.tab_access.includes(tab.id)}
                          onChange={() => toggleTabAccess(tab.id)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span>{tab.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Functional Areas — WQ-309 RAG retrieval grants */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
                  Functional Areas — RAG Retrieval ({createForm.functional_areas.length} selected)
                </h3>
                <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <small style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                    Areas a user with this role may retrieve from. No areas selected = no retrieval access (empty-until-granted).
                  </small>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '6px' }}>
                    {functionalAreas.map(area => (
                      <label
                        key={area.area_key}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
                          fontSize: '13px', padding: '4px 8px', borderRadius: '4px',
                          background: createForm.functional_areas.includes(area.area_key)
                            ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={createForm.functional_areas.includes(area.area_key)}
                          onChange={() => toggleFunctionalArea(area.area_key)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span>{area.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Can Create Roles */}
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
                  Can Create Roles ({createForm.can_create_roles.length} selected)
                </h3>
                <div
                  style={{
                    padding: '12px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                  }}
                >
                  <small style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                    Select which roles this role can assign to users:
                  </small>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {ASSIGNABLE_ROLES.map(role => (
                      <label
                        key={role}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          background: createForm.can_create_roles.includes(role)
                            ? 'rgba(59, 130, 246, 0.15)'
                            : 'var(--bg-tertiary)',
                          border: createForm.can_create_roles.includes(role)
                            ? '1px solid rgba(59, 130, 246, 0.3)'
                            : '1px solid var(--border)',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={createForm.can_create_roles.includes(role)}
                          onChange={() => toggleCanCreateRole(role)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span>{role}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowCreateModal(false)}
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateRole}
                disabled={isCreating || !createForm.name || !createForm.display_name}
              >
                {isCreating ? (
                  <>
                    <span className="spinner" style={{ width: '14px', height: '14px' }} />
                    Creating...
                  </>
                ) : (
                  <>
                    <CheckIcon size={16} />
                    Create Role
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditModal && editingRole && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div
            className="modal"
            style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Edit Role: {editingRole.display_name}</h2>
              <button className="btn btn-ghost" onClick={() => setShowEditModal(false)}>×</button>
            </div>

            <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
              {/* System Role Warning */}
              {editingRole.is_system && (
                <div
                  className="alert alert-warning"
                  style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '8px' }}
                >
                  <AlertIcon size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <strong>System Role</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>
                      This is a built-in system role. Changes may affect core platform functionality.
                      Only Super Admins can modify system roles.
                    </p>
                  </div>
                </div>
              )}

              {editError && (
                <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                  {editError}
                </div>
              )}

              {/* Basic Information */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
                  Basic Information
                </h3>
                <div className="form-group">
                  <label>Role Name (slug)</label>
                  <input
                    type="text"
                    className="input"
                    value={editingRole.name}
                    disabled
                    style={{ opacity: 0.6, cursor: 'not-allowed' }}
                  />
                  <small style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                    Role names cannot be changed after creation.
                  </small>
                </div>
                <div className="form-group">
                  <label>Display Name *</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g., Content Manager"
                    value={editForm.display_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, display_name: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="input"
                    placeholder="Optional description of this role's purpose"
                    rows={2}
                    value={editForm.description}
                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                    style={{ resize: 'vertical', minHeight: '60px' }}
                  />
                </div>
              </div>

              {/* Permissions */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
                  Permissions ({editForm.permissions.length} selected)
                </h3>
                <div style={{ display: 'grid', gap: '16px' }}>
                  {PERMISSION_CATEGORIES.map(category => (
                    <div
                      key={category.id}
                      style={{
                        padding: '12px',
                        background: 'var(--bg-secondary)',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                      }}
                    >
                      <div style={{ fontWeight: 500, marginBottom: '8px', fontSize: '13px' }}>
                        {category.name}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '6px' }}>
                        {category.permissions.map(perm => (
                          <label
                            key={perm.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              background: editForm.permissions.includes(perm.id)
                                ? 'rgba(59, 130, 246, 0.1)'
                                : 'transparent',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={editForm.permissions.includes(perm.id)}
                              onChange={() => toggleEditPermission(perm.id)}
                              style={{ cursor: 'pointer' }}
                            />
                            <span>{perm.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tab Access */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
                  Tab Access ({editForm.tab_access.length} selected)
                </h3>
                <div
                  style={{
                    padding: '12px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                  }}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '6px' }}>
                    {AVAILABLE_TABS.map(tab => (
                      <label
                        key={tab.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          background: editForm.tab_access.includes(tab.id)
                            ? 'rgba(59, 130, 246, 0.1)'
                            : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={editForm.tab_access.includes(tab.id)}
                          onChange={() => toggleEditTabAccess(tab.id)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span>{tab.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Functional Areas — WQ-309 RAG retrieval grants */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
                  Functional Areas — RAG Retrieval ({editForm.functional_areas.length} selected)
                </h3>
                <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <small style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                    Areas a user with this role may retrieve from. No areas selected = no retrieval access (empty-until-granted).
                  </small>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '6px' }}>
                    {functionalAreas.map(area => (
                      <label
                        key={area.area_key}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
                          fontSize: '13px', padding: '4px 8px', borderRadius: '4px',
                          background: editForm.functional_areas.includes(area.area_key)
                            ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={editForm.functional_areas.includes(area.area_key)}
                          onChange={() => toggleEditFunctionalArea(area.area_key)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span>{area.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Can Create Roles */}
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
                  Can Create Roles ({editForm.can_create_roles.length} selected)
                </h3>
                <div
                  style={{
                    padding: '12px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                  }}
                >
                  <small style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                    Select which roles this role can assign to users:
                  </small>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {ASSIGNABLE_ROLES.map(role => (
                      <label
                        key={role}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          background: editForm.can_create_roles.includes(role)
                            ? 'rgba(59, 130, 246, 0.15)'
                            : 'var(--bg-tertiary)',
                          border: editForm.can_create_roles.includes(role)
                            ? '1px solid rgba(59, 130, 246, 0.3)'
                            : '1px solid var(--border)',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={editForm.can_create_roles.includes(role)}
                          onChange={() => toggleEditCanCreateRole(role)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span>{role}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowEditModal(false)}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateRole}
                disabled={isUpdating || !editForm.display_name}
              >
                {isUpdating ? (
                  <>
                    <span className="spinner" style={{ width: '14px', height: '14px' }} />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckIcon size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && confirmDialogConfig && (
        <div className="modal-overlay" onClick={() => setShowConfirmDialog(false)}>
          <div
            className="modal"
            style={{ maxWidth: '420px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AlertIcon size={20} style={{ color: 'var(--error)' }} />
                {confirmDialogConfig.title}
              </h2>
              <button className="btn btn-ghost" onClick={() => setShowConfirmDialog(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                {confirmDialogConfig.message}
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancel
              </button>
              <button
                className={`btn ${confirmDialogConfig.confirmVariant === 'danger' ? 'btn-error' : 'btn-primary'}`}
                onClick={confirmDialogConfig.onConfirm}
              >
                {confirmDialogConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Dialog */}
      {showErrorDialog && errorDialogConfig && (
        <div className="modal-overlay" onClick={() => setShowErrorDialog(false)}>
          <div
            className="modal"
            style={{ maxWidth: '480px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header" style={{ borderBottom: '1px solid rgba(239, 68, 68, 0.2)', background: 'rgba(239, 68, 68, 0.05)' }}>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--error)' }}>
                <AlertIcon size={20} />
                {errorDialogConfig.title}
              </h2>
              <button className="btn btn-ghost" onClick={() => setShowErrorDialog(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '15px', marginBottom: errorDialogConfig.details || errorDialogConfig.suggestion ? '16px' : '0' }}>
                {errorDialogConfig.message}
              </p>
              {errorDialogConfig.details && (
                <p style={{
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  marginBottom: errorDialogConfig.suggestion ? '16px' : '0',
                  padding: '12px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                }}>
                  {errorDialogConfig.details}
                </p>
              )}
              {errorDialogConfig.suggestion && (
                <div style={{
                  fontSize: '13px',
                  padding: '12px',
                  background: 'rgba(59, 130, 246, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                }}>
                  <span style={{ color: 'var(--primary)', flexShrink: 0 }}>💡</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{errorDialogConfig.suggestion}</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => setShowErrorDialog(false)}
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesPage;
